import React, { useState, useEffect } from 'react';
import ContentHeaderSection from 'shared/common/ContentHeaderSection';
import FeatureLimitDisplay from 'shared/common/FeatureLimitDisplay';

const AccountSettingsContent = ({ useMockData = false }) => {
  const [selectedTab, setSelectedTab] = useState('userProfile');
  const [userProfile, setUserProfile] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [userRole, setUserRole] = useState('');

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('en-US', {
      dateStyle: 'full',
      timeStyle: 'short',
    });
  };

  const mockTutor = {
    bio: 'Curious educator in STEM fields.',
    createdDate: '2025-08-06T16:44:28.286Z',
    updatedDate: '2025-08-10T11:20:15.000Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  };

  const mockParent = {
    bio: 'Engaged parent supporting student growth.',
    createdDate: '2025-08-01T12:00:00.000Z',
    updatedDate: '2025-08-09T17:45:30.000Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  };

  useEffect(() => {
    const profileString = sessionStorage.getItem('app_userprofile');
    if (!profileString) return;

    const userData = JSON.parse(profileString);
    setUserProfile(userData);

    const userId = userData?.id;
    const role = userData?.zoneinfo ?? '';
    setUserRole(role);

    if (!userId) return;

    if (useMockData) {
      setUserSettings(role === 'Tutor' ? mockTutor : mockParent);
    } else {
      const endpoint =
        role === 'Tutor'
          ? `/api/tutors?userId.equals=${userId}`
          : `/api/parents?userId.equals=${userId}`;

      fetch(endpoint)
        .then(res => res.json())
        .then(data => setUserSettings(data[0] || null))
        .catch(err => console.error(`Failed to fetch ${role} settings:`, err));
    }
  }, [useMockData]);

  const renderDetailRow = (label, value) => (
    <div className="flex py-3 border-b border-gray-100 dark:border-gray-700">
      <div className="w-[30%] text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </div>
      <div className="w-[70%] text-sm text-gray-900 dark:text-gray-100">
        {value || '-'}
      </div>
    </div>
  );

  const renderRightContent = () => {
    if (selectedTab === 'userProfile' && userProfile) {
      return (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">User Profile</h2>
          <div>
            {renderDetailRow('User', userProfile.login)}
            {renderDetailRow('Name', `${userProfile.firstName} ${userProfile.lastName}`)}
            {renderDetailRow('Email', userProfile.email)}
            {renderDetailRow('Activated', userProfile.activated ? 'Yes' : 'No')}
            {renderDetailRow('Language', userProfile.langKey)}
            {renderDetailRow('Created Date', formatDate(userProfile.createdDate))}
          </div>
        </div>
      );
    }

    if (selectedTab === 'userSettings' && userSettings) {
      return (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">User Settings ({userRole})</h2>
          <div>
            {renderDetailRow('Bio', userSettings.bio)}
            {renderDetailRow('Subscription Plan', userSettings.subscriptionPlan ? userSettings.subscriptionPlan : 'Basic')}
          </div>
        </div>
      );
    }

    return <p className="text-gray-600 dark:text-gray-400">Choose a tab to view details.</p>;
  };

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={<div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Account Settings</div>}
        subtitle="Manage your profile and preferences"
      />

      <div className="flex flex-col md:flex-row gap-8 mt-6">
        {/* Left Menu */}
        <div className="w-full md:w-[30%] bg-gray-100 dark:bg-gray-900 p-4 rounded-xl border dark:border-gray-700 space-y-4">
          <button
            onClick={() => setSelectedTab('userProfile')}
            className={`w-full text-left px-4 py-2 rounded-lg transition font-medium ${selectedTab === 'userProfile'
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
              : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
          >
            User Profile
          </button>
          <button
            onClick={() => setSelectedTab('userSettings')}
            className={`w-full text-left px-4 py-2 rounded-lg transition font-medium ${selectedTab === 'userSettings'
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
              : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
          >
            User Settings
          </button>
        </div>

        {/* Right Content Panel */}
        <div className="w-full md:w-[70%] bg-white dark:bg-gray-800 rounded-xl shadow p-6 min-h-screen">
          {renderRightContent()}
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsContent;
